import { useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Sidebar } from './sidebar';
import { GlobalHeader } from './global-header';
import { SeasonalEffect } from '@/components/seasonal-effect';
import { ImpersonationBanner } from './impersonation-banner';
import { useSidebarStore } from '@/stores/sidebar';
import { useAuthStore } from '@/stores/auth';
import { usePermission, useIsSuperAdmin } from '@/lib/permissions';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { PLACEHOLDER_PERMISSION } from '@/lib/permissions';

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { isCollapsed, toggle } = useSidebarStore();
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  const showPlaceholder = usePermission(PLACEHOLDER_PERMISSION);
  const isSuperAdmin = useIsSuperAdmin();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Super admin can access /settings/* even with placeholder enabled (escape hatch)
    if (showPlaceholder && !(isSuperAdmin && location.pathname.startsWith('/settings'))) {
      navigate({ to: '/placeholder' });
    }
  }, [showPlaceholder, isSuperAdmin, location.pathname, navigate]);

  // Keyboard shortcut for sidebar toggle (Cmd/Ctrl + B)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        toggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggle]);

  return (
    <div className={cn('min-h-screen bg-background', isImpersonating && 'pt-9')}>
      <ImpersonationBanner />
      <SeasonalEffect />
      <Sidebar />
      <main
        className={cn(
          'min-h-screen transition-all duration-200 ease-in-out',
          isCollapsed ? 'ml-14' : 'ml-60'
        )}
      >
        <GlobalHeader />
        {children}
      </main>
    </div>
  );
}

